import React, { useState } from 'react';
import { DB } from '../data/database';
import { convertCurrency, calculateContainerTotalJPY, formatPrice } from '../utils/currency';
import CurrencySelect from './CurrencySelect';
import { CheckIcon, PlusIcon } from './Icons';

const FIXED_COST_FIELDS = [
  { key: 'freight',      label: 'Ocean Freight',     hint: 'Total sea freight for this container' },
  { key: 'drayage',      label: 'Drayage',           hint: 'Truck from yard/depot to port terminal' },
  { key: 'port_charges', label: 'Port / THC',        hint: 'Terminal handling charge at origin port' },
  { key: 'bl_fee',       label: 'Bill of Lading',    hint: 'B/L issuance fee' },
  { key: 'loading_fee',  label: 'Loading & Lashing', hint: 'Cost to load and secure units in container' },
];

function ContainerCostsEditor({ container, onSave }) {
  const [costs, setCosts] = useState(() => {
    const c = container.costs || { fixed: {}, custom: [] };
    // Ensure all 5 fixed keys exist
    const fixed = { ...c.fixed };
    FIXED_COST_FIELDS.forEach(f => {
      if (!fixed[f.key]) fixed[f.key] = { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: new Date().toISOString() };
    });
    return { fixed, custom: c.custom || [] };
  });
  const [dirty, setDirty] = useState(false);

  const unitCount = container.units?.length || 1;
  const totalJPY = calculateContainerTotalJPY(costs);
  const perUnitJPY = unitCount > 0 ? Math.round(totalJPY / unitCount) : 0;
  const perUnitUSD = Math.round(convertCurrency(perUnitJPY, 'JPY', 'USD') * 100) / 100;

  // Update a fixed cost field
  const setFixed = (key, field, value) => {
    setCosts(prev => ({
      ...prev,
      fixed: {
        ...prev.fixed,
        [key]: { ...(prev.fixed[key] || {}), [field]: value, updated_at: new Date().toISOString() }
      }
    }));
    setDirty(true);
  };

  // Custom items handlers
  const addCustomItem = () => {
    setCosts(prev => ({
      ...prev,
      custom: [...prev.custom, {
        id: 'CI-' + Date.now(),
        name: '',
        quantity: 1,
        unit_cost: 0,
        currency: 'JPY',
        notes: '',
        status: 'provisional',
        updated_at: new Date().toISOString(),
      }]
    }));
    setDirty(true);
  };

  const updateCustomItem = (idx, field, value) => {
    setCosts(prev => ({
      ...prev,
      custom: prev.custom.map((item, i) => i === idx ? { ...item, [field]: value, updated_at: new Date().toISOString() } : item)
    }));
    setDirty(true);
  };

  const removeCustomItem = (idx) => {
    setCosts(prev => ({ ...prev, custom: prev.custom.filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const handleSave = () => {
    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c =>
      c.id === container.id ? { ...c, costs } : c
    ));
    setDirty(false);
    if (onSave) onSave();
  };

  const handleCancel = () => {
    const c = container.costs || { fixed: {}, custom: [] };
    const fixed = { ...c.fixed };
    FIXED_COST_FIELDS.forEach(f => {
      if (!fixed[f.key]) fixed[f.key] = { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: new Date().toISOString() };
    });
    setCosts({ fixed, custom: c.custom || [] });
    setDirty(false);
  };

  // Helper to show JPY equivalent for non-JPY amounts
  const jpyEquiv = (amount, currency) => {
    if (!amount || currency === 'JPY') return null;
    const jpyVal = Math.round(convertCurrency(Number(amount), currency, 'JPY'));
    return <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 8 }}>{'\u2248'} {'\u00A5'}{jpyVal.toLocaleString()}</span>;
  };

  const inputStyle = { fontSize: 13, padding: '6px 10px', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text1)', width: '100%' };
  const amountInputStyle = { ...inputStyle, width: 120, textAlign: 'right', fontFamily: 'monospace' };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Fixed Costs Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Fixed Costs
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FIXED_COST_FIELDS.map(({ key, label, hint }) => {
            const cost = costs.fixed[key] || { amount: 0, currency: 'JPY', notes: '' };
            return (
              <div key={key} style={{ padding: '12px 14px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{label}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{hint}</div>
                  </div>
                  {jpyEquiv(cost.amount, cost.currency)}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={cost.amount || ''}
                    onChange={e => setFixed(key, 'amount', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    style={amountInputStyle}
                  />
                  <CurrencySelect
                    value={cost.currency}
                    onChange={v => setFixed(key, 'currency', v)}
                  />
                  <input
                    value={cost.notes || ''}
                    onChange={e => setFixed(key, 'notes', e.target.value)}
                    placeholder="Notes (optional)"
                    style={{ ...inputStyle, flex: 1, minWidth: 120 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom Items Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Custom Items
          </div>
          <button onClick={addCustomItem} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <PlusIcon /> Add Item
          </button>
        </div>

        {costs.custom.length === 0 ? (
          <div style={{ padding: '20px 16px', background: 'var(--bg2)', borderRadius: 10, border: '1px dashed var(--border)', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
            No custom items yet. Click + Add Item to add plywood, lashing, or other container-specific costs.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {costs.custom.map((item, idx) => {
              const lineTotal = (Number(item.quantity) || 1) * (Number(item.unit_cost) || 0);
              return (
                <div key={item.id || idx} style={{ padding: '10px 14px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      value={item.name || ''}
                      onChange={e => updateCustomItem(idx, 'name', e.target.value)}
                      placeholder="Item name"
                      style={{ ...inputStyle, flex: 2, minWidth: 120 }}
                    />
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={e => updateCustomItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                      placeholder="Qty"
                      min="1"
                      style={{ ...inputStyle, width: 60, textAlign: 'center' }}
                    />
                    <span style={{ color: 'var(--text3)', fontSize: 12 }}>{'\u00D7'}</span>
                    <input
                      type="number"
                      value={item.unit_cost || ''}
                      onChange={e => updateCustomItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                      placeholder="Unit cost"
                      style={{ ...amountInputStyle, width: 100 }}
                    />
                    <CurrencySelect
                      value={item.currency}
                      onChange={v => updateCustomItem(idx, 'currency', v)}
                    />
                    <input
                      value={item.notes || ''}
                      onChange={e => updateCustomItem(idx, 'notes', e.target.value)}
                      placeholder="Notes"
                      style={{ ...inputStyle, flex: 1, minWidth: 80 }}
                    />
                    <button onClick={() => removeCustomItem(idx)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 4, fontSize: 14 }}>{'\u00D7'}</button>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    Line total: {lineTotal.toLocaleString()} {item.currency || 'JPY'}
                    {jpyEquiv(lineTotal, item.currency)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary Section */}
      <div style={{ padding: '16px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Container Total</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text1)' }}>{'\u00A5'}{totalJPY.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Per Unit ({unitCount} units)</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{'\u00A5'}{perUnitJPY.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Per Unit (USD)</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--cyan)' }}>${perUnitUSD.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {dirty && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={handleCancel} className="btn btn-secondary btn-sm">Cancel</button>
          <button onClick={handleSave} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckIcon /> Save Changes
          </button>
        </div>
      )}
    </div>
  );
}

export default ContainerCostsEditor;
